import React, { useCallback } from 'react';
import styled from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';
import ReactParallaxTilt from 'react-parallax-tilt';
import { colors } from '../styles/colors.ts';

const Container = styled.div`
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    background-color: ${colors.neutral[200]}30;
    border-radius: 3rem;
    padding: 2rem;
    max-width: ${props => props.maxWidth || '100%'};
    margin: 0 auto;

    @media (max-width: 768px) {
        border-radius: 2rem;
        padding: 1rem;
    }
`;

const EmblaContainer = styled.div`
    overflow: hidden;
    width: 100%;
    aspect-ratio: ${props => props.aspectRatio ? props.aspectRatio : (props.height ? 'auto' : '5/2')};
    height: ${props => props.height ? props.height : 'auto'};
    border-radius: 2rem;

    @media (max-width: 768px) {
        border-radius: 1rem;
        aspect-ratio: ${props => props.mobileContain ? 'auto' : (props.aspectRatio ? props.aspectRatio : (props.mobileHeight ? 'auto' : '4/4'))};
        height: ${props => props.mobileHeight ? props.mobileHeight : 'auto'};
    }
`;

const CondensedContainer = styled(Container)`
    padding: 0;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: ${(colors.transparent)};

    @media (max-width: 768px) {
        gap: 0.5rem;
    }
`;

const CondensedEmblaContainer = styled(EmblaContainer)`
    aspect-ratio: ${props => props.aspectRatio ? props.aspectRatio : (props.height ? 'auto' : '21/9')};
    border-radius: 1rem;

    @media (max-width: 768px) {
        aspect-ratio: ${props => props.mobileContain ? 'auto' : (props.aspectRatio ? props.aspectRatio : (props.mobileHeight ? 'auto' : '16/9'))};
        height: ${props => props.mobileHeight ? props.mobileHeight : 'auto'};
        border-radius: 0.75rem;
    }
`;

const EmblaViewport = styled.div`
    overflow: hidden;
    width: 100%;
    height: 100%;
`;

const EmblaSlide = styled.div`
    flex: 0 0 100%;
    min-width: 0;
    position: relative;
    height: 100%;
`;

const EmblaSlideImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: ${props => props.contain ? 'contain' : 'cover'};
    border-radius: 1rem;
    cursor: pointer;
`;

const ThumbnailsContainer = styled.div`
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
    padding: 0 1rem;
    overflow: hidden;
`;

const ThumbnailsViewport = styled.div`
    display: flex;
    gap: 1rem;
    width: 100%;
`;

const ThumbnailsContainerInner = styled.div`
    display: flex;
    gap: 1rem;
`;

const ThumbnailButton = styled.button`
    flex: 0 0 100px;
    height: 100px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    opacity: ${props => props.selected ? 1 : 0.5};
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }   

    @media (max-width: 768px) {
        flex: 0 0 80px;
        height: 80px;
    }
`;

const ThumbnailImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.5rem;
`;

const NavigationContainer = styled.div`
    display: flex;
    flex-direction: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? 'row' : 'column'};
    gap: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? '1rem' : '1rem'};
    justify-content: space-between;
    align-items: center;
    width: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? 'calc(100% - 2rem)' : 'auto'};
    position: relative;

    @media (max-width: 768px) {
        flex-direction: column;
        width: auto;
    }
`;

const NavigationButton = styled.button`
	position: relative;
	background-color: ${colors.white};
	border: solid 1px ${colors.neutral[200]};
	color: ${colors.black};
	width: 40px;
	height: 40px;
	border-radius: 10rem;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 1rem;
	transition: background-color 0.3s ease-in-out;

	&:hover {
		background-color: ${colors.neutral[100]};
	}

	&:disabled {
		background-color: ${colors.neutral[100]};
		color: ${colors.neutral[400]};
		cursor: not-allowed;
	}
`;

const NavigationButtonsGroup = styled.div`
    display: flex;
    gap: 0.5rem;
    order: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? '1' : '2'};

    @media (max-width: 768px) {
        order: 2;
    }
`;

const SlideCount = styled.div`
    text-align: center;
    font-size: 0.875rem;
    color: ${colors.neutral[600]};
    margin-top: 0.5rem;
    order: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? '3' : '3'};

    @media (max-width: 768px) {
        order: 3;
        margin-top: 0;
    }
`;

const ImageLabel = styled.div`
    padding: 0.4rem 1rem;
    width: fit-content;
    margin: 0 auto;
    background: ${(colors.neutral[100])}99;
    -webkit-backdrop-filter: blur(1rem);
    backdrop-filter: blur(1rem);
    color: ${colors.black};
    font-size: 0.8rem;
    text-align: center;
    border-radius: 2rem;
    order: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? '2' : '1'};
    position: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? 'absolute' : 'unset'};
    transform: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? 'translateX(-50%)' : 'unset'};
    left: ${props => (props.condensed && props.stretchControls && !props.isMobile) ? '50%' : 'unset'};

    @media (max-width: 768px) {
        order: 1;
        margin: 1rem auto;
        position: unset;
        transform: unset;
        left: unset;
    }
`;

const CarouselTitle = styled.h2`
    font-size: 1.2rem;
    text-align: center;
    margin-bottom: 1.5rem;
    color: ${colors.black};
    width: 100%;
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 1.2rem;
        margin-bottom: 1rem;
    }
`;

const CarouselSection = styled.div`
    display: grid;
    grid-template-columns: repeat(${props => props.columns || 3}, 1fr);
    gap: 2rem;
    width: 100%;
    max-width: ${props => props.maxWidth || '100%'};
    margin: 2rem auto;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 4rem;
    }
`;

const Carrousel = ({ images, mobileImages, onImageClick, condensed = false, stretchControls = false, label, height, mobileHeight, contain = false, mobileContain = false, aspectRatio, maxWidth, title }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mainViewportRef, emblaApi] = useEmblaCarousel({
    loop: true,
    dragFree: false,
    align: 'start',
    slidesToScroll: 1,
    skipSnaps: false,
  });
  const [thumbViewportRef, thumbEmblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    align: 'start',
    slidesToScroll: 1,
    loop: false,
    skipSnaps: false,
  });

  const onThumbClick = useCallback(
    (index) => {
      if (!emblaApi || !thumbEmblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, thumbEmblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    setSelectedIndex(newIndex);
    if (!condensed && thumbEmblaApi) {
      thumbEmblaApi.scrollTo(newIndex);
    }
  }, [emblaApi, thumbEmblaApi, condensed]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderImages = () => {
    const imagesToUse = mobileImages && window.innerWidth <= 768 ? mobileImages : images;
    const containToUse = window.innerWidth <= 768 ? mobileContain : contain;
    return imagesToUse.map((src, index) => (
      <EmblaSlide key={index}>
        <EmblaSlideImg
          src={src}
          alt={`Slide ${index + 1}`}
          contain={containToUse}
          onClick={() => onImageClick && onImageClick({
            src,
            alt: `Slide ${index + 1}`,
            index,
            label: Array.isArray(label) ? label[index] : label
          })}
        />
      </EmblaSlide>
    ));
  };

  const renderThumbnails = () => {
    const imagesToUse = mobileImages && window.innerWidth <= 768 ? mobileImages : images;
    return imagesToUse.map((src, index) => (
      <ThumbnailButton
        key={index}
        selected={index === selectedIndex}
        onClick={() => onThumbClick(index)}
      >
        <ThumbnailImg src={src} alt={`Thumbnail ${index + 1}`} />
      </ThumbnailButton>
    ));
  };

  return (
    <ReactParallaxTilt
      tiltMaxAngleX={1}
      tiltMaxAngleY={1}
    >
      {condensed ? (
        <CondensedContainer maxWidth={maxWidth}>
          {title && <CarouselTitle>{title}</CarouselTitle>}
          <CondensedEmblaContainer 
            height={height} 
            mobileHeight={mobileHeight} 
            aspectRatio={aspectRatio}
            mobileContain={mobileContain}
          >
            <EmblaViewport ref={mainViewportRef}>
              <div style={{ display: 'flex', height: '100%' }}>
                {renderImages()}
              </div>
            </EmblaViewport>
          </CondensedEmblaContainer>
          <NavigationContainer height={height} mobileHeight={mobileHeight} condensed={condensed} stretchControls={stretchControls} isMobile={isMobile}>
            <NavigationButtonsGroup condensed={condensed} stretchControls={stretchControls} isMobile={isMobile}>
              <NavigationButton onClick={scrollPrev}>
                ←
              </NavigationButton>
              <NavigationButton onClick={scrollNext}>
                →
              </NavigationButton>
            </NavigationButtonsGroup>
            {label && (
              <ImageLabel condensed={condensed} stretchControls={stretchControls} isMobile={isMobile}>
                {Array.isArray(label) ? label[selectedIndex] : label}
              </ImageLabel>
            )}
            <SlideCount condensed={condensed} stretchControls={stretchControls} isMobile={isMobile}>
              {selectedIndex + 1} / {(mobileImages && window.innerWidth <= 768 ? mobileImages : images).length}
            </SlideCount>
          </NavigationContainer>
        </CondensedContainer>
      ) : (
        <Container maxWidth={maxWidth}>
          {title && <CarouselTitle>{title}</CarouselTitle>}
          <EmblaContainer 
            height={height} 
            mobileHeight={mobileHeight} 
            aspectRatio={aspectRatio}
            mobileContain={mobileContain}
          >
            <EmblaViewport ref={mainViewportRef}>
              <div style={{ display: 'flex', height: '100%' }}>
                {renderImages()}
              </div>
            </EmblaViewport>
          </EmblaContainer>
          <ThumbnailsContainer>
            <ThumbnailsViewport ref={thumbViewportRef}>
              <ThumbnailsContainerInner>
                {renderThumbnails()}
              </ThumbnailsContainerInner>
            </ThumbnailsViewport>
          </ThumbnailsContainer>
          {label && (
            <ImageLabel>
              {Array.isArray(label) ? label[selectedIndex] : label}
            </ImageLabel>
          )}
        </Container>
      )}
    </ReactParallaxTilt>
  );
};

export { CarouselSection };
export default Carrousel;
